import { ReactNode } from 'react';

/**
 * Condition that allows caller flexibility to determine whether to show or hide
 * content, and to provide diagnostic information about why content is hidden
 * when it is.
 */
export interface PassCondition {
  /** Whether this condition passes. When `false`, the associated @see reason is included in diagnostics. */
  showIf: boolean;
  /** Diagnostic label reported in `data-reasons` when @see showIf is `false` (e.g. `'capability-disabled'`). */
  reason: string;
}

export interface ConditionalDisplayProps {
  /** Base identifier for the diagnostic element. A `-hidden` suffix is appended automatically (e.g. `'report-ali-discrepancy-button'` → `data-name="report-ali-discrepancy-button-hidden"`). */
  dataName: string;
  /** Array of conditions that must all pass (AND). Failed @see Condition.reason values are comma-joined in the `data-reasons` attribute of the hidden diagnostic span. */
  conditions: PassCondition[];
  /** Content rendered when every condition in @see conditions passes. */
  children: ReactNode;
}

/**
 * Conditionally renders children when all conditions pass, or a hidden
 * diagnostic `<span>` when any condition fails. The span carries
 * `data-name="{dataName}-hidden"` and `data-reasons` listing the failed
 * condition reasons, making failures visible in Playwright via `getByTestId`.
 */
export default function ConditionalDisplay({
  dataName,
  conditions,
  children,
}: ConditionalDisplayProps) {
  const failedReasons = conditions
    .filter(condition => !condition.showIf)
    .map(condition => condition.reason);

  if (failedReasons.length > 0) {
    return (
      <span
        data-name={`${dataName}-hidden`}
        data-reasons={failedReasons.join(',')}
        hidden
      />
    );
  }

  return <>{children}</>;
}
